import { useEffect, useRef, MutableRefObject } from 'react';

import { Connector, ConnectorOptions, InitMessage, InitRejectedMessage, InitResponseMessage } from '../connector/Interface';

const PROTOCOL_VERSION = 1;

interface MessageTypeSpec {
    type: string;
    version: number;
}

export interface ConnectorSetup<C extends Connector> {
    factory: () => C;
    initOptions: ConnectorOptions;
    requiredMessageTypes: ReadonlyArray<MessageTypeSpec>;
    /** Called once the Init handshake is accepted. Register domain listeners here. */
    onHandshakeComplete: (connector: C) => void;
}

/**
 * Manages the full lifecycle of a postMessage connector:
 * creates, initialises, performs the Init handshake, and cleans up on unmount.
 * Returns a stable ref to the live connector instance.
 */
export function usePostMessageConnector<C extends Connector>(
    setup: ConnectorSetup<C>,
): MutableRefObject<C | null> {
    const connectorRef = useRef<C | null>(null);
    // Capture setup at mount time so the effect closure is stable.
    const setupRef = useRef(setup);

    useEffect(() => {
        const { factory, initOptions, requiredMessageTypes, onHandshakeComplete } = setupRef.current;
        const connector = factory();

        function checkInitResponse(msg: InitResponseMessage): void {
            if (msg.chosenProtocolVersion !== PROTOCOL_VERSION) {
                connector.send(new InitRejectedMessage(`Unsupported protocol version: ${msg.chosenProtocolVersion}`));
                return;
            }
            for (const { type, version } of requiredMessageTypes) {
                if (!msg.offeredMessageTypes[type]?.includes(version)) {
                    connector.send(new InitRejectedMessage(`${type} v${version} not supported`));
                    return;
                }
            }
            onHandshakeComplete(connector);
        }

        connector.onMessage(InitRejectedMessage.type, (msg) => {
            console.warn('Connection rejected:', (msg as InitRejectedMessage).reason);
        });
        connector.onMessage(InitResponseMessage.type, (msg) => checkInitResponse(msg as InitResponseMessage));

        connector.init(initOptions);

        const initMsg = new InitMessage();
        initMsg.supportedProtocolVersions = [PROTOCOL_VERSION];
        requiredMessageTypes.forEach(({ type, version }) => {
            initMsg.requestingMessageTypes[type] = [version];
        });
        connector.send(initMsg);

        connectorRef.current = connector;
        return connector.close.bind(connector);
    }, []);

    return connectorRef;
}
